import React, { useState, useEffect } from "react";
import {
  Grid, Card, TextField, Button, Typography, FormControl,
  InputLabel, Select, MenuItem, RadioGroup, FormControlLabel,
  Radio, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, Container, IconButton, Snackbar, Alert, Autocomplete
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import Notes from "./header";
import FooterManual from "./footerManual";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PublicIcon from '@mui/icons-material/Public';
import AnchorIcon from '@mui/icons-material/Anchor';
import Divider from '@mui/material/Divider';
import { useSnackbar } from "notistack";
import { fetchWishlist, moveToWishlistFromCart, fetchCart } from "./store";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Tooltip from '@mui/material/Tooltip';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlineIcon from '@mui/icons-material/LightbulbOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const DeliverySection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });
  const { enqueueSnackbar } = useSnackbar();
  const [paymentInitiated, setPaymentInitiated] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressError, setAddressError] = useState("");

  const [selectedAddress, setSelectedAddress] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [deliveryOption, setDeliveryOption] = useState("");
  const [carToRemove, setCarToRemove] = useState(null);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);
  const [isAddressEditable, setIsAddressEditable] = useState(false);

  const [shippingCalculations, setShippingCalculations] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);

  const [removeCarDialog, setRemoveCarDialog] = useState({
    open: false,
    modelId: null,
  });
  const [moveSuccessDialog, setMoveSuccessDialog] = useState(false);
  const [shippingOptionSelected, setShippingOptionSelected] = useState(false);

  const [countryCodes, setCountryCodes] = useState([
    { code: "+1", name: "USA (+1)" },
    { code: "+91", name: "India (+91)" },
    { code: "+44", name: "UK (+44)" },
    { code: "+86", name: "China (+86)" },
    { code: "+49", name: "Germany (+49)" },
    { code: "+81", name: "Japan (+81)" },

    // Add more country codes as needed
  ]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    countryCode: "",
    mobileNumber: "",
    country: "",
    seaPort: "",
    city: "",
    state: "",
    pincode: ""
  });

  // Modify the shipping method selection handler
  const handleShippingOptionSelect = async (optionValue) => {
    setShipmentData(prev => ({
      ...prev,
      shippingOption: optionValue
    }));
    setShippingOptionSelected(true);

    // Call the API for shipping option selection
    await handleShippingOptionApiCall(optionValue);
  };

  const handleShippingOptionApiCall = async (shippingOptionType) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    // Get selected car model IDs
    const selectedCarModelIds = cars.filter(car => car.selected).map(car => car.id);
    if (selectedCarModelIds.length === 0) {
      setSnackbar({ open: true, message: "No cars selected", severity: "warning" });
      return;
    }

    // Get port ID from selected port
    const selectedPort = portsList.find(port => port.portName === formData.seaPort);
    if (!selectedPort) {
      setSnackbar({ open: true, message: "Please select a port", severity: "warning" });
      return;
    }

    // Get address ID from selected address
    if (!selectedAddress) {
      setSnackbar({ open: true, message: "Please select an address", severity: "warning" });
      return;
    }

    setLoadingShipping(true);

    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/authorization/shipping/calculate-multiple-cars-order',
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            carModelIds: selectedCarModelIds,
            portId: selectedPort.portId,
            shippingOptionType: shippingOptionType,
            addressId: parseInt(selectedAddress),
            orderPlaced: false
          })
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Update the shipment data with the API response
      setShipmentData(prev => ({
        ...prev,
        totalCost: {
          carBasePrice: `$${data.totalCostOfBasicCarPrice.toFixed(2)}`,
          shippingCost: `$${data.totalCostAllCars.toFixed(2)}`,
          total: `$${data.totalCost.toFixed(2)}`
        }
      }));

      // Store the full API response for later use
      setShippingCalculations(data);

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to calculate shipping costs",
        severity: "error"
      });
    } finally {
      setLoadingShipping(false);
    }
  };

  // Function to handle payment API call
  const handlePaymentApiCall = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    // Get selected car model IDs
    const selectedCarModelIds = cars.filter(car => car.selected).map(car => car.id);
    if (selectedCarModelIds.length === 0) {
      setSnackbar({ open: true, message: "No cars selected", severity: "warning" });
      return;
    }

    // Get port ID from selected port
    const selectedPort = portsList.find(port => port.portName === formData.seaPort);
    if (!selectedPort) {
      setSnackbar({ open: true, message: "Please select a port", severity: "warning" });
      return;
    }

    // Get address ID from selected address
    if (!selectedAddress) {
      setSnackbar({ open: true, message: "Please select an address", severity: "warning" });
      return;
    }

    setLoadingShipping(true);

    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/authorization/shipping/calculate-multiple-cars-order',
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            carModelIds: selectedCarModelIds,
            portId: selectedPort.portId,
            shippingOptionType: shipmentData.shippingOption,
            addressId: parseInt(selectedAddress),
            orderPlaced: true  // This is the only difference for payment call
          })
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Update the shipment data with the API response
      setShipmentData(prev => ({
        ...prev,
        totalCost: {
          carBasePrice: `$${data.totalCost.toFixed(2)}`,
          shippingCost: `$${data.totalCostAllCars.toFixed(2)}`,
          total: `$${data.totalBaseShippingCostAll.toFixed(2)}`
        }
      }));

      // Store the full API response for later use
      setShippingCalculations(data);

      // Proceed with payment process
      setPaymentInitiated(true);
      navigate("/payment", {
        state: {
          totalPayable: data.totalCost,
          shippingDetails: shipmentData,
          shippingCalculations: data,
          selectedAddress: addresses.find(addr => addr.addressId.toString() === selectedAddress),
          cars: cars.filter(car => car.selected)
        }
      });

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to process payment",
        severity: "error"
      });
    } finally {
      setLoadingShipping(false);
    }
  };


  useEffect(() => {
    const handleBackButton = (e) => {
      if (!paymentInitiated) {
        e.preventDefault();
        setSnackbar({
          open: true,
          message: "Please complete your payment before leaving",
          severity: "warning"
        });
      }
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [paymentInitiated]);

  // Add useEffect to prevent navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!paymentInitiated) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [paymentInitiated]);

  // const handlePaymentClick = async () => {
  //   if (selectedAddress && shipmentData.shippingOption && !loadingShipping) {
  //     try {
  //       // First, save the calculation history
  //       const token = localStorage.getItem("authToken");
  //       if (!token) {
  //         setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
  //         return;
  //       }

  //       if (!shippingCalculations) {
  //         throw new Error("Shipping calculations not available");
  //       }

  //       const selectedCarModelIds = cars.filter(car => car.selected).map(car => car.id);

  //       // Prepare the request data with carModelIds included
  //       const requestData = {
  //         ...shippingCalculations,
  //         carModelIds: selectedCarModelIds // Add car model IDs to the request
  //       };

  //       // Prepare the request data for saving calculation history
  //       const saveHistoryResponse = await fetch(
  //         'https://car.amar.co.jp/api/authorization/calculate-order-history/save',
  //         {
  //           method: 'POST',
  //           headers: {
  //             'accept': 'application/json',
  //             'Authorization': `Bearer ${token}`,
  //             'Content-Type': 'application/json'
  //           },
  //           body: JSON.stringify(requestData)
  //         }
  //       );

  //       if (!saveHistoryResponse.ok) {
  //         throw new Error("Failed to save calculation history");
  //       }

  //       // If successful, proceed to payment
  //       setPaymentInitiated(true);
  //       navigate("/payment", {
  //         state: {
  //           totalPayable: shipmentData.totalCost.total,
  //           shippingDetails: shipmentData,
  //           shippingCalculations: shippingCalculations,
  //           selectedAddress: addresses.find(addr => addr.addressId.toString() === selectedAddress),
  //           cars: cars.filter(car => car.selected)
  //         }
  //       });
  //     } catch (error) {
  //       setSnackbar({
  //         open: true,
  //         message: error.message || "Failed to save calculation history",
  //         severity: "error"
  //       });
  //     }
  //   }
  // };

  const handlePaymentClick = async () => {
    await handlePaymentApiCall();
  };
  
  const [shipmentData, setShipmentData] = useState({
    country: "",
    seaPort: "",
    shippingOption: "",
    items: [],
    fees: {
      insurance: "$0",
      documentation: "$0",
      currencyAdjustment: "$0",
      bunkerAdjustment: "$0",
      terminalHandling: "$0",
      customsClearance: "$0"
    },
    totalCost: {
      carBasePrice: "$0",
      shippingCost: "$0",
      total: "$0"
    }
  });
  const countries = ["United States", "India", "China", "Germany"];
  const seaPorts = ["Port of Shanghai", "Port of Singapore", "Port of Los Angeles"];

  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch ports when Port Dropdown is opened
  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { port: null })
    }));
  };

  // LocationPill - Displays country/port in a compact chip-like format
  const LocationPill = ({ label, value }) => (
    <Grid item xs={12} md={6}>
      <Box sx={{
        border: '1px solid #eee',
        borderRadius: '20px',
        p: '8px 16px',
        display: 'inline-block'
      }}>
        <Typography variant="subtitle2" color="text.secondary">{label}:</Typography>
        <Typography variant="body1" fontWeight="bold">{value}</Typography>
      </Box>
    </Grid>
  );

  // ToggleButtonStyle - Styled shipping option buttons
  const toggleButtonStyle = {
    '&.Mui-selected': {
      backgroundColor: '#d02026',
      color: 'white',
      '&:hover': { backgroundColor: '#b01c22' }
    },
    borderColor: '#d02026',
    color: '#d02026',
    px: 3,
    py: 1
  };

  // CostPreview - Displays costs in a compact horizontal format
  const CostPreview = ({ base, shipping, m3Cost, total }) => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      mt: 1,
      gap: 1,
      flexWrap: 'wrap'
    }}>
      <CostChip label="Base Item Cost" value={base} />
      <CostChip label="m³ Charge" value={m3Cost} />
      <CostChip label="Port Charges" value={shipping} emphasized />
      <CostChip label="Total Payable" value={total} emphasized />
    </Box>
  );

  // CostChip - Individual cost display component
  const CostChip = ({ label, value, emphasized }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography sx={{
        fontWeight: emphasized ? 'bold' : 'normal',
        color: emphasized ? '#d02026' : 'inherit'
      }}>
        {value}
      </Typography>
    </Box>
  );

  // VehicleHeader component - displays vehicle information
  const VehicleHeader = ({ item }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        {item.make} {item.model} ({item.year})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        ID: {item.id}
      </Typography>
    </Box>
  );

  // FeeBreakdown component - shows detailed fee information
  const FeeBreakdown = ({ insurance, documentation, customs }) => (
    <Box sx={{
      backgroundColor: '#f9f9f9',
      p: 2,
      borderRadius: 1,
      borderLeft: '3px solid #d02026'
    }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        FEE DETAILS
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Insurance:</Typography>
        <Typography variant="body2">${insurance.toFixed(2)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Documentation:</Typography>
        <Typography variant="body2">${documentation.toFixed(2)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2">Customs Clearance:</Typography>
        <Typography variant="body2">${customs.toFixed(2)}</Typography>
      </Box>
    </Box>
  );

  // SummaryCard component - displays summary information in a card
  const SummaryCard = ({ title, items, highlight = false }) => (
    <Grid item xs={12} md={6}>
      <Card sx={{
        p: 2,
        border: highlight ? '2px solid #d02026' : '1px solid #eee',
        backgroundColor: highlight ? '#fff9f9' : 'white'
      }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          {title}
        </Typography>
        {items.map((item, index) => (
          <Box key={index} sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1,
            ...(item.emphasized && {
              borderTop: '1px solid #eee',
              pt: 1,
              mt: 1
            })
          }}>
            <Typography variant="body2" fontWeight={item.emphasized ? 'bold' : 'normal'}>
              {item.label}:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={item.emphasized ? 'bold' : 'normal'}
              color={item.emphasized ? '#d02026' : 'inherit'}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Card>
    </Grid>
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      const userDetails = await fetchUserDetails();
      if (userDetails) {
        setFormData(prev => ({
          ...prev,
          firstName: userDetails.firstName || "",
          lastName: userDetails.lastName || ""
        }));
      }
      await fetchUserAddresses();
      await fetchCountries();
    };

    fetchInitialData();
  }, []);

  const fetchUserDetails = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    try {
      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/user/get/user/details",
        {
          method: "GET",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        return data.userDetails;
      } else {
        throw new Error(data.message || "Failed to fetch user details");
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
      return null;
    }
  };

  const fetchUserAddresses = async () => {
    setLoadingAddresses(true);
    setAddressError("");
    const token = localStorage.getItem("authToken");

    if (!token) {
      setAddressError("Unauthorized! Please log in.");
      setLoadingAddresses(false);
      return;
    }

    try {
      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/user/get-user-addresses",
        {
          method: "GET",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        // Ensure addresses is always an array, even if empty
        setAddresses(Array.isArray(data.addresses) ? data.addresses : []);
      } else if (response.status === 404) {
        setAddressError("No address, Please add address.");
        setAddresses([]);
      } else {
        throw new Error(data.message || "Failed to fetch addresses");
      }
    } catch (error) {
      setAddressError(error.message);
      setSnackbar({ open: true, message: error.message, severity: "error" });
      setAddresses([]); // Ensure addresses is set to empty array on error
    } finally {
      setLoadingAddresses(false);
    }
  };

  const deleteAddress = async (addressId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/user/delete-addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        setSnackbar({ open: true, message: data.message || "Address deleted successfully", severity: "success" });
        fetchUserAddresses();
      } else {
        throw new Error(data.message || "Failed to delete address");
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    } finally {
      setConfirmDeleteDialog(false);
    }
  };

  const fetchCartItems = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://car.amar.co.jp/api/master/car-cart-and-whishlist/get/items?type=ADD_IN_CART",
        {
          method: "GET",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cart items");
      }

      const data = await response.json();

      const mappedCars = data.items.map(item => ({
        id: item.modelId,
        make: item.brand,
        model: item.modelName,
        year: item.registrationDate,
        price: `$${item.price}`,
        selected: true
      }));
      setCars(mappedCars);
      updateTotalCost(mappedCars);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCartItems = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          "https://car.amar.co.jp/api/master/car-cart-and-whishlist/get/items?type=ADD_IN_CART",
          {
            method: "GET",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch cart items");
        }

        const data = await response.json();

        const mappedCars = data.items.map(item => ({
          id: item.modelId,
          make: item.brand,
          model: item.modelName,
          year: item.registrationDate,
          price: `$${item.price}`,
          selected: true
        }));
        setCars(mappedCars);
        updateTotalCost(mappedCars);
      } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, []);

  const updateTotalCost = (selectedCars) => {
    const selectedCarsList = selectedCars.filter(car => car.selected);

    // Calculate individual item costs
    const itemsWithCosts = selectedCarsList.map(car => {
      const basePrice = parseInt(car.price.replace(/[^0-9]/g, ''), 0) || 0;
      const shippingCost = 0;
      const total = basePrice + shippingCost;

      return {
        id: car.id,
        make: car.make,
        model: car.model,
        year: car.year,
        basePrice: `$${basePrice.toLocaleString()}`,
        shippingCost: `$${shippingCost.toLocaleString()}`,
        total: `$${total.toLocaleString()}`
      };
    });

    // Calculate totals
    const basePriceTotal = selectedCarsList.reduce((sum, car) => {
      return sum + (parseInt(car.price.replace(/[^0-9]/g, ''), 0) || 0);
    }, 0);

    const shippingCostTotal = selectedCarsList.length;
    const totalCost = basePriceTotal + shippingCostTotal;

    setShipmentData(prev => ({
      ...prev,
      items: itemsWithCosts,
      totalCost: {
        carBasePrice: `$${basePriceTotal.toLocaleString()}`,
        shippingCost: `$${shippingCostTotal.toLocaleString()}`,
        total: `$${totalCost.toLocaleString()}`
      }
    }));
  };

  useEffect(() => {
    if (deliveryOption) {
      fetchUserAddresses();
    }
  }, [deliveryOption]);

  const sectionTitleStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: "#d02026",
    marginBottom: "20px",
    borderBottom: "2px solid #d02026",
    paddingBottom: "10px"
  };

  const cardStyle = {
    padding: "25px",
    marginBottom: "30px",
    border: "2px solid #ebebeb",
    borderRadius: "8px",
    backgroundColor: "#ffffff"
  };

  const buttonStyle = {
    backgroundColor: "#d02026",
    color: "white",
    fontWeight: "bold",
    padding: "12px 24px",
    "&:hover": { backgroundColor: "#b51a1f" }
  };

  // LocationCard component
  const LocationCard = ({ icon, title, value }) => (
    <Card sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      border: '1px solid #eee',
      borderRadius: '8px'
    }}>
      <Box sx={{
        backgroundColor: '#ffebee',
        p: '8px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography fontWeight="bold">{value}</Typography>
      </Box>
    </Card>
  );

  // FeeItem component
  const FeeItem = ({ label, value }) => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      p: '8px 0',
      borderBottom: '1px solid #f0f0f0'
    }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" fontWeight="bold">{value}</Typography>
    </Box>
  );

  // VehicleSummary component
  const VehicleSummary = ({ make, model, year, price }) => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      mb: 1
    }}>
      <Typography variant="body2">
        {make} {model} ({year})
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {price}
      </Typography>
    </Box>
  );

  const handleAddressSelect = (event) => {
    const selectedId = event.target.value;
    setSelectedAddress(selectedId);
    setIsAddressEditable(false);

    if (selectedId && selectedId !== "new") {
      // Safely find the address
      const address = addresses.find(addr => addr?.addressId?.toString() === selectedId);
      if (address) {
        setFormData({
          ...formData,
          addressLine1: address.addressLine1 || "",
          addressLine2: address.addressLine2 || "",
          city: address.city || "",
          state: address.state || "",
          pincode: address.pincode || "",
          country: address.countryName || "",
          seaPort: address.portName || "",
          countryCode: address.countryCode || "",
          mobileNumber: address.mobileNumber || ""
        });

        setShipmentData(prev => ({
          ...prev,
          country: address.countryName || "",
          seaPort: address.portName || ""
        }));

        // Safely find country and fetch ports
        const country = countriesList.find(c => c?.countryName === address.countryName);
        if (country?.countryId) {
          fetchPorts(country.countryId);
        }
      }
    }
  };

  const handleAddNewAddress = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      setSnackbar({
        open: true,
        message: "Unauthorized! Please log in.",
        severity: "error"
      });
      return;
    }

    try {
      // First check if user can add a new address
      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/user/add-user-address",
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            addressLine1: "string",
            addressLine2: "string",
            mobileNumber: "+283-8073949319131",
            city: "string",
            state: "string",
            pincode: "string",
            countryName: "string",
            portName: "string"
          })
        }
      );

      const data = await response.json();

      if (response.status === 400) {
        if (data.message === "You can add up to 3 address only.") {
          setSnackbar({
            open: true,
            message: "You can save a maximum of 3 addresses. To add a new one, please remove the oldest address from your saved list.",
            severity: "warning"
          });
        } else if (data.message === "Selected country does not have any port.") {
          setSelectedAddress("new");
          setIsAddressEditable(true); // Enable editing
          // Focus on Address Line 1 field without showing error message
          const addressLine1Field = document.querySelector('input[label="Address Line 1"]');
          if (addressLine1Field) {
            addressLine1Field.focus();
          }
        } else {
          throw new Error(data.message || "Failed to add new address");
        }
      } else {
        throw new Error(data.message || "Failed to add new address");
      }
    } catch (error) {
      // Only show error if it's not the "no port" message
      if (error.message !== "Selected country does not have any port.") {
        setSnackbar({
          open: true,
          message: error.message,
          severity: "error"
        });
      }
    }
  };

  const handleFormSubmit = async () => {
    // Validate all required fields
    if (!formData.addressLine1.trim()) {
      setSnackbar({ open: true, message: "Address Line 1 is required", severity: "error" });
      return;
    }

    if (!formData.city.trim()) {
      setSnackbar({ open: true, message: "City is required", severity: "error" });
      return;
    }

    if (!formData.state.trim()) {
      setSnackbar({ open: true, message: "State is required", severity: "error" });
      return;
    }

    if (!formData.pincode.trim()) {
      setSnackbar({ open: true, message: "Pincode is required", severity: "error" });
      return;
    }

    if (!formData.country.trim()) {
      setSnackbar({ open: true, message: "Country is required", severity: "error" });
      return;
    }

    if (!formData.seaPort.trim()) {
      setSnackbar({ open: true, message: "Seaport is required", severity: "error" });
      return;
    }

    if (!formData.countryCode.trim()) {
      setSnackbar({ open: true, message: "Country code is required", severity: "error" });
      return;
    }

    if (!formData.mobileNumber.trim()) {
      setSnackbar({ open: true, message: "Mobile number is required", severity: "error" });
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    // Format the mobile number with country code
    const formattedMobileNumber = `${formData.countryCode}-${formData.mobileNumber}`;

    try {
      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/user/add-user-address",
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
            countryName: formData.country,
            portName: formData.seaPort,
            mobileNumber: formattedMobileNumber
          })
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        setSnackbar({ open: true, message: "Address saved successfully", severity: "success" });
        await fetchUserAddresses();

        if (data.address) {
          const newAddress = data.address;
          setSelectedAddress(newAddress.addressId.toString());
          setFormData({
            ...formData,
            addressLine1: newAddress.addressLine1,
            addressLine2: newAddress.addressLine2,
            city: newAddress.city,
            state: newAddress.state,
            pincode: newAddress.pincode,
            country: newAddress.countryName,
            seaPort: newAddress.portName,
            // Extract country code and mobile number from the saved address
            countryCode: newAddress.mobileNumber.split('-')[0],
            mobileNumber: newAddress.mobileNumber.split('-').slice(1).join('-')
          });

          setShipmentData({
            ...shipmentData,
            country: newAddress.countryName,
            seaPort: newAddress.portName
          });
        }
        setIsAddressEditable(false);
      } else {
        throw new Error(data.message || "Failed to save address");
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    }
  };

  const handleDeleteClick = (addressId, e) => {
    e.stopPropagation();
    setAddressToDelete(addressId);
    setConfirmDeleteDialog(true);
  };

  const confirmDeleteAddress = () => {
    if (addressToDelete) {
      deleteAddress(addressToDelete);
    }
  };

  const handleCarSelect = (carId) => {
    const updatedCars = cars.map(car => ({
      ...car,
      selected: car.id === carId ? !car.selected : car.selected
    }));
    setCars(updatedCars);
    updateTotalCost(updatedCars);
  };

  const handleRemoveCar = (modelId) => {
    // Check if this is the last item in the cart
    if (cars.length === 1) {
      setSnackbar({
        open: true,
        message: "Cannot remove the last item from cart",
        severity: "warning"
      });
      return;
    }

    setRemoveCarDialog({
      open: true,
      modelId: modelId
    });
  };

  const moveToWishlist = async () => {
    // Check if this is the last item in the cart
    if (cars.length === 1) {
      setSnackbar({
        open: true,
        message: "Cannot move the last item to wishlist",
        severity: "warning"
      });
      setRemoveCarDialog({ open: false, modelId: null });
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${removeCarDialog.modelId}&SelectType=MOVE_TO_WISHLIST`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            modelId: removeCarDialog.modelId,
            type: "MOVE_TO_WISHLIST",
          }),
        }
      );

      const data = await response.json();
      if (data.code === 200) {
        enqueueSnackbar("Moved to wishlist successfully", { variant: "success" });
        dispatch(fetchCart()); // Update Redux cart state
        dispatch(fetchWishlist()); // Update Redux Wishlist

        // Update local state by removing the moved item
        const updatedCars = cars.filter(car => car.id !== removeCarDialog.modelId);
        setCars(updatedCars);
        updateTotalCost(updatedCars); // Update shipment data with remaining items

        setRemoveCarDialog({ open: false, modelId: null }); // Close the dialog
      } else {
        enqueueSnackbar(data.message || "Failed to move to wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error moving to wishlist", { variant: "error" });
      console.error("Error moving to wishlist:", error);
    } finally {
      setRemoveCarDialog({ open: false, modelId: null }); // Ensure dialog closes even if there's an error
    }
  };

  const removeFromCart = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized! Please log in.", severity: "error" });
      return;
    }

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${removeCarDialog.modelId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        setSnackbar({ open: true, message: "Item removed from cart successfully", severity: "success" });
        dispatch(fetchCart());

        // Update local state by removing the deleted item
        const updatedCars = cars.filter(car => car.id !== removeCarDialog.modelId);
        setCars(updatedCars);
        updateTotalCost(updatedCars); // Update shipment data with remaining items

      } else {
        throw new Error(data.message || "Failed to remove from cart");
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    } finally {
      setRemoveCarDialog({ open: false, modelId: null });
    }
  };

  const confirmRemoveCar = () => {
    const updatedCars = cars.filter(car => car.id !== carToRemove);
    setCars(updatedCars);
    updateTotalCost(updatedCars);
    setRemoveCarDialog(false);
    setCarToRemove(null);
  };

  const handleDeliveryOptionChange = (event) => {
    setDeliveryOption(event.target.value);
  };

  const formatAddress = (address) => {
    return `${address.addressLine1}, ${address.addressLine2}, ${address.city}, ${address.state}, ${address.pincode}, ${address.countryName}, ${address.portName}`;
  };

  const calculateShippingCosts = async (shippingOption = shipmentData.shippingOption) => {
    // Check if we have the required data
    if (!formData.seaPort || cars.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select Address.",
        severity: "warning"
      });
      return;
    }

    setLoadingShipping(true);

    try {
      // Find the port ID from the portsList
      const selectedPort = portsList.find(port => port.portName === formData.seaPort);
      if (!selectedPort) {
        throw new Error("Selected port not found");
      }

      // Prepare the request data
      const requestData = {
        carModelIds: cars.map(car => car.id),
        portId: selectedPort.portId,
        shippingOption: shippingOption
      };

      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/shipping/calculate/bulk",
        {
          method: "POST",
          headers: {
            "accept": "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData)
        }
      );

      const data = await response.json();

      // Check if response is valid
      if (!data || !data.summary) {
        throw new Error("Invalid response from server");
      }

      setShippingCalculations(data);

      // Safely access summary properties with fallback values
      const summary = data.summary || {};
      setShipmentData(prev => ({
        ...prev,
        fees: {
          insurance: `$${(summary.totalInsuranceCostAll || 0).toFixed(2)}`,
          documentation: `$${(summary.totalDocumentationFeeAll || 0).toFixed(2)}`,
          currencyAdjustment: `$${((summary.totalCurrencyAdjustmentFeeAll || 0)).toFixed(2)}`,
          bunkerAdjustment: `$${(summary.totalBunkerAdjustmentFactorAll || 0).toFixed(2)}`,
          terminalHandling: `$${(summary.totalTerminalHandlingChargeAll || 0).toFixed(2)}`,
          customsClearance: `$${(summary.totalCustomsClearanceFeeAll || 0).toFixed(2)}`,
          m3Cost: `$${(summary.totalBaseShippingCostAll || 0).toFixed(2)}`
        },
        totalCost: {
          carBasePrice: `$${((summary.totalCostAllCars || 0) - (summary.totalBaseShippingCostAll || 0) - (summary.totalInsuranceCostAll || 0) - (summary.totalDocumentationFeeAll || 0)
            - (summary.totalCurrencyAdjustmentFeeAll || 0) - (summary.totalBunkerAdjustmentFactorAll || 0) - (summary.totalTerminalHandlingChargeAll || 0)
            - (summary.totalCustomsClearanceFeeAll || 0)).toFixed(2)}`,

          shippingCost: `$${((summary.totalInsuranceCostAll || 0) + (summary.totalDocumentationFeeAll || 0)
            + (summary.totalCurrencyAdjustmentFeeAll || 0) + (summary.totalBunkerAdjustmentFactorAll || 0) + (summary.totalTerminalHandlingChargeAll || 0)
            + (summary.totalCustomsClearanceFeeAll || 0)).toFixed(2)}`,

          m3Cost: `$${(summary.totalBaseShippingCostAll || 0).toFixed(2)}`,

          total: `$${(summary.totalCostAllCars || 0).toFixed(2)}`
        }
      }));

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to calculate shipping costs",
        severity: "error"
      });
    } finally {
      setLoadingShipping(false);
    }
  };

  return (
    <div>
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>
      <Box sx={{ padding: "40px 20px", backgroundColor: "#f8f9fa" }}>
        <Container maxWidth="lg">
          <Grid container justifyContent="center" spacing={4}>
            {/* Cart Items Section - Always displayed at the top */}
            <Grid item xs={12} sm={10} md={8}>
              <Card sx={cardStyle}>
                <Typography variant="h6" fontSize={{ xs: "1rem", md: "1.5rem" }} fontWeight={700}>
                  Cart Items
                </Typography>
                {loading ? (
                  <Typography>Loading cart items...</Typography>
                ) : cars.length === 0 ? (
                  <Typography>No items in cart</Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell>Make</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell align="center" sx={{ width: "150px" }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cars.map((car) => (
                          <TableRow key={car.id}>
                            <TableCell>{car.make}</TableCell>
                            <TableCell>{car.model}</TableCell>
                            <TableCell>{car.year}</TableCell>
                            <TableCell>{car.price}</TableCell>
                            <TableCell align="center" sx={{ padding: "8px 16px" }}>
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveCar(car.id)}
                                size="small"
                                sx={{
                                  whiteSpace: "nowrap",
                                  minWidth: "auto",
                                  padding: "4px 8px",
                                  ...(cars.length === 1 && {
                                    backgroundColor: '#f5f5f5',
                                    color: '#999',
                                    borderColor: '#ddd',
                                    cursor: 'not-allowed',
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  })
                                }}
                                disabled={cars.length === 1}
                              >
                                Remove From Cart
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </Grid>

            {/* Delivery Option Selection */}
            <Grid item xs={12} md={8}>
              <Card sx={cardStyle}>
                <Typography sx={sectionTitleStyle}>Select Delivery Option</Typography>
                <RadioGroup
                  row
                  value={deliveryOption}
                  onChange={handleDeliveryOptionChange}
                >
                  <FormControlLabel
                    value="port"
                    control={<Radio sx={{ color: "#d02026" }} />}
                    label="Port Delivery (for Shipment)"
                  />
                  <FormControlLabel
                    value="local"
                    control={<Radio sx={{ color: "#d02026" }} />}
                    label="Self Pickup / Local Transport (for Local)"
                  />
                </RadioGroup>
              </Card>
            </Grid>

            {/* Saved Addresses Section - Only visible when an option is selected */}
            {deliveryOption && (
              <Grid item xs={12} md={8}>
                <Card sx={cardStyle}>
                  <Typography sx={sectionTitleStyle}>Saved Addresses</Typography>
                  {loadingAddresses ? (
                    <Typography>Loading addresses...</Typography>
                  ) : addressError ? (
                    <Typography color="error">{addressError}</Typography>
                  ) : (
                    <FormControl fullWidth sx={{ marginBottom: "20px" }}>
                      <InputLabel>Select Address</InputLabel>
                      <Select
                        value={selectedAddress}
                        onChange={handleAddressSelect}
                        label="Select Address"
                        sx={selectStyle}
                        disabled={!deliveryOption || addresses.length === 0}
                      >
                        {addresses.map((address) => (
                          <MenuItem key={address.addressId} value={address.addressId.toString()}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <span>{formatAddress(address)}</span>
                              <IconButton
                                onClick={(e) => handleDeleteClick(address.addressId, e)}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Box>
                          </MenuItem>
                        ))}
                        {addresses.length === 0 && (
                          <MenuItem disabled>No saved addresses</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  )}

                  <Button
                    variant="contained"
                    sx={buttonStyle}
                    onClick={handleAddNewAddress}
                    disabled={!deliveryOption || loadingAddresses}
                  >
                    {loadingAddresses ? "Checking..." : "ADD NEW ADDRESS"}
                  </Button>
                </Card>
              </Grid>
            )}

            {/* Address Form Section - Only visible when an option is selected */}
            {deliveryOption && (
              // Updated "New Address" form section with improved UI
              <Grid item xs={12} md={8}>
                <Card sx={{
                  ...cardStyle,
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <Typography sx={{
                    ...sectionTitleStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ flex: 1 }}>New Address</span>
                    {selectedAddress === "new" && (
                      <span style={{
                        fontSize: '0.8rem',
                        backgroundColor: '#d02026',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}>
                        Editing New Address
                      </span>
                    )}
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Name Fields */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    {/* Address Line Fields */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 1"
                        variant="outlined"
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.addressLine1}
                        onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                        disabled={!isAddressEditable}
                        onFocus={() => {
                          if (!isAddressEditable) {
                            setSnackbar({
                              open: true,
                              message: "Please click 'ADD NEW ADDRESS' first",
                              severity: "info"
                            });
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 2 (Optional)"
                        variant="outlined"
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.addressLine2}
                        onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                        disabled={!isAddressEditable}
                      />
                    </Grid>




                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={countryCodes}
                          getOptionLabel={(option) => option.name}
                          value={countryCodes.find(c => c.code === formData.countryCode) || null}
                          onChange={(event, newValue) => {
                            setFormData({
                              ...formData,
                              countryCode: newValue ? newValue.code : ""
                            });
                          }}
                          disabled={!isAddressEditable}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Country Code"
                              variant="outlined"
                              required
                              sx={{
                                ...textFieldStyle,
                                '& .MuiInputLabel-root': {
                                  color: '#888',
                                  '&.Mui-focused': { color: '#d02026' }
                                }
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        variant="outlined"
                        required
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.mobileNumber}
                        onChange={(e) => {
                          // Basic validation to ensure only numbers and allowed characters
                          const value = e.target.value.replace(/[^0-9+-]/g, '');
                          setFormData({ ...formData, mobileNumber: value });
                        }}
                        disabled={!isAddressEditable}
                        inputProps={{
                          maxLength: 15 // Adjust as needed based on your requirements
                        }}
                      />
                    </Grid>



                    {/* Country and Port Fields */}
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={countriesList}
                        getOptionLabel={(option) => option.countryName}
                        value={countriesList.find(c => c.countryName === formData.country) || null}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            country: newValue ? newValue.countryName : "",
                            seaPort: "" // Reset port when country changes
                          });
                          if (newValue) {
                            fetchPorts(newValue.countryId);
                          }
                        }}
                        disabled={!isAddressEditable}
                        onOpen={() => {
                          if (countriesList.length === 0) fetchCountries();
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Country"
                            variant="outlined"
                            required
                            sx={{
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              ...textFieldStyle,
                              '& .MuiInputLabel-root': {
                                color: '#888',
                                '&.Mui-focused': { color: '#d02026' }
                              }
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={portsList}
                        getOptionLabel={(option) => option.portName}
                        value={portsList.find(p => p.portName === formData.seaPort) || null}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            seaPort: newValue ? newValue.portName : ""
                          });
                        }}
                        disabled={!formData.country || !isAddressEditable}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Sea Port"
                            variant="outlined"
                            required
                            sx={{
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              ...textFieldStyle,
                              '& .MuiInputLabel-root': {
                                color: '#888',
                                '&.Mui-focused': { color: '#d02026' }
                              }
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* City, State, Pincode Fields */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="City"
                        variant="outlined"
                        required
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        disabled={!isAddressEditable}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="State/Province"
                        variant="outlined"
                        required
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        disabled={!isAddressEditable}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Postal/Zip Code"
                        variant="outlined"
                        required
                        sx={{
                          ...textFieldStyle,
                          '& .MuiInputLabel-root': {
                            color: '#888',
                            '&.Mui-focused': { color: '#d02026' }
                          }
                        }}
                        value={formData.pincode}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                        disabled={!isAddressEditable}
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12} sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2
                    }}>
                      <Button
                        variant="contained"
                        sx={{
                          ...buttonStyle,
                          padding: '12px 30px',
                          fontSize: '1rem',
                          borderRadius: '8px',
                          textTransform: 'none',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            backgroundColor: '#b51a1f',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          }
                        }}
                        onClick={handleFormSubmit}
                        disabled={!isAddressEditable}
                      >
                        Save Address
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            )}

            {/* Shipment Form Section - Only visible when Port Delivery is selected */}

            {deliveryOption === "port" && (
              <Grid item xs={12} md={8}>
                <Card sx={{
                  ...cardStyle,
                  borderLeft: '4px solid #d02026',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <Typography sx={{
                    ...sectionTitleStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <LocalShippingIcon sx={{ fontSize: '1.8rem' }} />
                    Shipment Summary
                  </Typography>

                  {/* Location Overview */}
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    mb: 3,
                    '& > div': {
                      flex: '1 1 200px',
                      minWidth: 0
                    }
                  }}>
                    <LocationCard
                      icon={<PublicIcon color="primary" />}
                      title="Destination Country"
                      value={shipmentData.country || "Not selected"}
                    />
                    <LocationCard
                      icon={<AnchorIcon color="primary" />}
                      title="Port of Delivery"
                      value={shipmentData.seaPort || "Not selected"}
                    />
                  </Box>

                  {/* Shipping Method Selector - Updated with innovative UI */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      gap: 1
                    }}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 'bold',
                        color: !shipmentData.shippingOption ? '#ff3d00' : 'text.primary',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {!shipmentData.shippingOption && (
                          <ErrorOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        )}
                        SHIPPING METHOD *
                      </Typography>
                      {!shipmentData.shippingOption && (
                        <Typography variant="caption" sx={{
                          color: '#ff3d00',
                          animation: 'fadeIn 0.5s ease-in-out'
                        }}>
                          (Required - Select to see costs)
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                      gap: 2
                    }}>
                      {[
                        { value: "CIF", label: "CIF", description: "Cost, Insurance & Freight" },
                        { value: "CNF", label: "C&F", description: "Cost & Freight" },
                        { value: "FOB", label: "FOB", description: "Free On Board" }
                      ].map((option) => (
                        <Card
                          key={option.value}
                          onClick={() => handleShippingOptionSelect(option.value)}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            border: shipmentData.shippingOption === option.value
                              ? '2px solid #d02026'
                              : '1px solid rgba(0, 0, 0, 0.12)',
                            backgroundColor: shipmentData.shippingOption === option.value
                              ? 'rgba(208, 32, 38, 0.08)'
                              : 'background.paper',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d02026',
                              boxShadow: '0 2px 8px rgba(208, 32, 38, 0.2)'
                            },
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {shipmentData.shippingOption === option.value && (
                            <Box sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: 0,
                              height: 0,
                              borderStyle: 'solid',
                              borderWidth: '0 32px 32px 0',
                              borderColor: 'transparent #d02026 transparent transparent'
                            }} />
                          )}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="bold" sx={{
                              color: shipmentData.shippingOption === option.value ? '#d02026' : 'text.primary',
                              mb: 0.5
                            }}>
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                          {shipmentData.shippingOption === option.value && (
                            <Box sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              color: '#d02026'
                            }}>
                              <CheckCircleIcon />
                            </Box>
                          )}
                        </Card>
                      ))}
                    </Box>

                    {/* Help text */}
                    <Typography variant="caption" sx={{
                      display: 'block',
                      mt: 1,
                      color: !shipmentData.shippingOption ? '#ff3d00' : 'text.secondary',
                      fontStyle: !shipmentData.shippingOption ? 'italic' : 'normal'
                    }}>
                      {!shipmentData.shippingOption
                        ? 'Please select your preferred shipping method to see cost breakdown'
                        : 'Select the shipping terms that apply to your order'}
                    </Typography>
                  </Box>

                  {/* Cost Breakdown - Only show when shipping option is selected */}
                  {shippingOptionSelected && (
                    <Box sx={{ mb: 3 }}>
                      <Card sx={{
                      backgroundColor: '#fff9f9',
                      border: '1px solid #d02026',
                      p: 2,
                      mt: 3
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        ORDER SUMMARY ({cars.filter(c => c.selected).length} ITEMS)
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="body1">Base Item Cost</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {loadingShipping ? "Calculating..." : shipmentData.totalCost.carBasePrice}
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="body1">Total Shipping Cost</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {loadingShipping ? "Calculating..." : shipmentData.totalCost.shippingCost}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="h6">Total Payable</Typography>
                          <Typography variant="h6" fontWeight="bold" color="#d02026">
                            {loadingShipping ? "Calculating..." : shipmentData.totalCost.total}
                          </Typography>
                        </Box>
                      </Box>
                      </Card>
                    </Box>
                  )}

                  {/* Final Summary - Only show when shipping option is selected */}
                  {/* {shippingOptionSelected && (
                    <Card sx={{
                      backgroundColor: '#fff9f9',
                      border: '1px solid #d02026',
                      p: 2,
                      mt: 3
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        ORDER SUMMARY ({cars.filter(c => c.selected).length} ITEMS)
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {shipmentData.items.map((item, index) => (
                          <VehicleSummary
                            key={index}
                            make={item.make}
                            model={item.model}
                            year={item.year}
                            price={item.basePrice}
                          />
                        ))}
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="h6" fontWeight="bold">
                          TOTAL PAYABLE
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="#d02026">
                          {loadingShipping ? "Calculating..." : shipmentData.totalCost.total}
                        </Typography>
                      </Box>
                    </Card>
                  )} */}

                  {/* Make Payment Button - Only show when shipping option is selected */}
                  {shippingOptionSelected && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip
                        title={
                          !selectedAddress ? "Please select an address" :
                            !shipmentData.shippingOption ? "Please select the shipping method" : ""
                        }
                        disableHoverListener={selectedAddress && shipmentData.shippingOption}
                      >
                        <span>
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: (selectedAddress && shipmentData.shippingOption) ? "#d02026" : "#cccccc",
                              color: "white",
                              fontWeight: "bold",
                              padding: "12px 30px",
                              fontSize: "1rem",
                              "&:hover": {
                                backgroundColor: (selectedAddress && shipmentData.shippingOption) ? "#b51a1f" : "#cccccc"
                              }
                            }}
                            disabled={!selectedAddress || !shipmentData.shippingOption || loadingShipping}
                            onClick={handlePaymentClick}
                          >
                            MAKE PAYMENT
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}

                </Card>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
      <FooterManual />

      {/* Dialog for confirming address deletion */}
      <Dialog open={confirmDeleteDialog} onClose={() => setConfirmDeleteDialog(false)}>
        <DialogTitle>Confirm Address Removal</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this address from your saved list?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialog(false)} sx={{ color: "#d02026" }}>
            ❌ Cancel
          </Button>
          <Button
            onClick={confirmDeleteAddress}
            sx={{ color: "#d02026" }}
            startIcon={<span>✅</span>}
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for removing car from cart */}
      <Dialog open={removeCarDialog.open} onClose={() => setRemoveCarDialog({ open: false, modelId: null })}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>What would you like to do with this item?</Typography>
          {cars.length === 1 && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              Note: You cannot remove the last item from your cart
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', padding: '16px 24px' }}>
          <Button
            onClick={moveToWishlist}
            sx={{
              color: "#d02026",
              border: "1px solid #d02026",
              '&:hover': {
                backgroundColor: '#ffebee'
              }
            }}
          >
            MOVE TO WISHLIST
          </Button>
          <Button
            onClick={removeFromCart}
            sx={{
              color: cars.length === 1 ? "#cccccc" : "#d02026",
              border: `1px solid ${cars.length === 1 ? "#cccccc" : "#d02026"}`,
              '&:hover': {
                backgroundColor: cars.length === 1 ? 'transparent' : '#ffebee'
              }
            }}
            disabled={cars.length === 1}
          >
            REMOVE FROM CART
          </Button>
          <Button
            onClick={() => setRemoveCarDialog({ open: false, modelId: null })}
            sx={{
              color: "#d02026",
              '&:hover': {
                backgroundColor: '#ffebee'
              }
            }}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success dialog for move to wishlist */}
      <Dialog open={moveSuccessDialog} onClose={() => setMoveSuccessDialog(false)}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>Item moved to wishlist successfully</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMoveSuccessDialog(false)}
            sx={{ color: "#d02026" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for showing messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#ebebeb" },
    "&:hover fieldset": { borderColor: "#d02026" },
    "&.Mui-focused fieldset": { borderColor: "#d02026" }
  }
};

const selectStyle = {
  border: '1px solid #ccc',
  borderRadius: '4px',
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#ebebeb" },
    "&:hover fieldset": { borderColor: "#d02026" },
    "&.Mui-focused fieldset": { borderColor: "#d02026" }
  }
};

const readOnlyFieldStyle = {
  "& .MuiInputBase-input": { color: "#333" },
  "& .MuiInputLabel-root": { color: "#d02026" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#ebebeb" },
    "&.Mui-focused fieldset": { borderColor: "#d02026" }
  }
};

export default DeliverySection;